import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { DICT, LANGS, translate } from './strings';
import type { LangCode, Strings } from './strings';

/*
 * THE CHOSEN LANGUAGE, AS ONE SOURCE OF TRUTH.
 *
 * A context carries the current language, its writing direction, a way to change it, and `t` — the
 * lookup a component calls instead of hard-coding English. The default value is a working English
 * context, so a component that reads `useLang()` with no provider above it still renders (English),
 * which is exactly what a unit test rendering one widget wants. The real provider adds the parts a
 * test does not need: persistence across visits, and stamping `lang`/`dir` on the document element
 * so the whole page reads in the right language and direction.
 */
export type Translate = (key: keyof Strings, params?: Record<string, string | number>) => string;

export interface LangState {
  readonly lang: LangCode;
  readonly dir: 'ltr' | 'rtl';
  readonly setLang: (lang: LangCode) => void;
  readonly t: Translate;
}

const STORAGE_KEY = 'vv.lang';

const defaultState: LangState = {
  lang: 'en',
  dir: 'ltr',
  setLang: () => {},
  t: (key, params) => translate('en', key, params),
};

const LangContext = createContext<LangState>(defaultState);

/** Is `value` a language we actually carry a dictionary for? Guards stored and browser-guessed values. */
function isLangCode(value: string | null | undefined): value is LangCode {
  return value != null && value in DICT;
}

/** The language to open with: a past choice if any, else the browser's preference, else English. */
function initialLang(): LangCode {
  if (typeof window !== 'undefined') {
    const stored = window.localStorage?.getItem(STORAGE_KEY);
    if (isLangCode(stored)) return stored;
    const preferred = window.navigator?.language?.slice(0, 2);
    if (isLangCode(preferred)) return preferred;
  }
  return 'en';
}

export function LangProvider({ children }: { children: React.ReactNode }): React.JSX.Element {
  const [lang, setLangState] = useState<LangCode>(initialLang);

  const setLang = useCallback((next: LangCode) => {
    setLangState(next);
    if (typeof window !== 'undefined') window.localStorage?.setItem(STORAGE_KEY, next);
  }, []);

  const dir = LANGS[lang].dir;

  /* The document itself carries the language and direction — for the browser's own text shaping, for
     a screen reader announcing the page's language, and for CSS that keys off `dir`. */
  useEffect(() => {
    if (typeof document === 'undefined') return;
    document.documentElement.lang = lang;
    document.documentElement.dir = dir;
  }, [lang, dir]);

  const value = useMemo<LangState>(
    () => ({ lang, dir, setLang, t: (key, params) => translate(lang, key, params) }),
    [lang, dir, setLang],
  );

  return <LangContext.Provider value={value}>{children}</LangContext.Provider>;
}

/** Read the current language, its direction, the setter, and `t`. Works with or without a provider. */
export function useLang(): LangState {
  return useContext(LangContext);
}
